import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Modal, ActionBtn } from './Shared';
import { rotaAPI } from '../services/api';

/* ------------------------------------------------------------------
 * @admin-block-range-v1 (30 Sep 2026) -- M-41
 *
 * Takes a block of hours out of a doctor's availability across a date
 * range. The preview changes nothing and the execute carries its count
 * back, so a patient booking in between aborts the run.
 *
 * This never cancels a booking. Windows that already hold an
 * appointment are listed and skipped -- emptying a booked day is the
 * clear-day button next to this one, which refunds.
 *
 * Άρση φραγής only removes blocks an admin made. A doctor's own breaks
 * stay where they are; the backend enforces that, not this screen.
 * ------------------------------------------------------------------ */

const DAYS = [
    { n: 1, label: 'Δε' }, { n: 2, label: 'Τρ' }, { n: 3, label: 'Τε' },
    { n: 4, label: 'Πε' }, { n: 5, label: 'Πα' }, { n: 6, label: 'Σα' },
    { n: 0, label: 'Κυ' },
];

const boxStyle = {
    padding: '10px 12px',
    background: 'var(--bg)',
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 14,
};

const rowStyle = { display: 'flex', justifyContent: 'space-between', padding: '4px 0' };
const fieldStyle = { width: '100%', padding: 8, fontSize: 14, fontFamily: 'inherit' };
const labelStyle = { display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--muted)' };

export default function BlockRangeModal({ open, onClose, doctorId, doctorName, defaultDate, onDone }) {
    const [form, setForm] = useState({
        startDate: '', endDate: '', startTime: '09:00', endTime: '17:00', stepMinutes: 15,
    });
    const [weekdays, setWeekdays] = useState([]);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [reason, setReason] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({
            startDate: defaultDate || '', endDate: defaultDate || '',
            startTime: '09:00', endTime: '17:00', stepMinutes: 15,
        });
        setWeekdays([]);
        setPreview(null);
        setResult(null);
        setReason('');
        setError('');
        setTouched(false);
    }, [open, defaultDate]);

    const loadPreview = useCallback(async () => {
        if (!doctorId || !form.startDate || !form.endDate) return;
        setLoading(true);
        setError('');
        setTouched(true);
        try {
            const params = {
                startDate: form.startDate, endDate: form.endDate,
                startTime: form.startTime, endTime: form.endTime,
                stepMinutes: form.stepMinutes,
            };
            if (weekdays.length > 0) params.weekdays = weekdays.join(',');
            const res = await rotaAPI.blockRangePreview(doctorId, params);
            setPreview(res.data.data);
        } catch (e) {
            setPreview(null);
            setError(e.response?.data?.error || 'Η προεπισκόπηση απέτυχε.');
        } finally {
            setLoading(false);
        }
    }, [doctorId, form, weekdays]);

    const toggleDay = (n) => {
        setPreview(null);
        setWeekdays((prev) => (prev.includes(n) ? prev.filter((d) => d !== n) : prev.concat(n)));
    };

    const setField = (key, value) => {
        setPreview(null);
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleRun = async () => {
        if (!preview || !reason.trim()) return;
        setRunning(true);
        try {
            const body = {
                startDate: form.startDate, endDate: form.endDate,
                startTime: form.startTime, endTime: form.endTime,
                stepMinutes: form.stepMinutes,
                reason: reason.trim(), confirm: true,
                expectedWindows: preview.expectedWindows,
            };
            if (weekdays.length > 0) body.weekdays = weekdays;
            const res = await rotaAPI.blockRange(doctorId, body);
            setResult(res.data.data);
            toast.success('Οι ώρες κλείστηκαν.');
        } catch (e) {
            if (e.response?.status === 409) {
                toast.error('Το εύρος άλλαξε από την προεπισκόπηση. Ελέγξτε τα νέα νούμερα.');
                await loadPreview();
            } else {
                toast.error(e.response?.data?.error || 'Η φραγή απέτυχε.');
            }
        } finally {
            setRunning(false);
        }
    };

    const handleUnblock = async () => {
        if (!form.startDate || !form.endDate) return;
        if (!window.confirm('Άρση κάθε φραγής που έχει βάλει διαχειριστής σε αυτό το εύρος ημερών;')) return;
        setRunning(true);
        try {
            const res = await rotaAPI.unblockRange(doctorId, {
                startDate: form.startDate, endDate: form.endDate, confirm: true,
            });
            toast.success('Αφαιρέθηκαν ' + res.data.data.removed + ' φραγές.');
            setPreview(null);
            if (onDone) onDone();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Η άρση απέτυχε.');
        } finally {
            setRunning(false);
        }
    };

    const handleClose = () => {
        if (result && onDone) onDone();
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose} title="Φραγή ωρών σε εύρος ημερών" size="md">
            <div style={{ padding: 20 }}>
                <div style={boxStyle}>
                    <div style={rowStyle}>
                        <span style={{ color: 'var(--muted)' }}>Ιατρός</span>
                        <strong>{preview?.doctor || doctorName || '—'}</strong>
                    </div>
                </div>

                {!result && (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                            <div>
                                <label style={labelStyle}>Από ημερομηνία</label>
                                <input type="date" style={fieldStyle} value={form.startDate}
                                    onChange={(e) => setField('startDate', e.target.value)} />
                            </div>
                            <div>
                                <label style={labelStyle}>Έως ημερομηνία</label>
                                <input type="date" style={fieldStyle} value={form.endDate}
                                    onChange={(e) => setField('endDate', e.target.value)} />
                            </div>
                            <div>
                                <label style={labelStyle}>Από ώρα</label>
                                <input type="time" style={fieldStyle} value={form.startTime}
                                    onChange={(e) => setField('startTime', e.target.value)} />
                            </div>
                            <div>
                                <label style={labelStyle}>Έως ώρα</label>
                                <input type="time" style={fieldStyle} value={form.endTime}
                                    onChange={(e) => setField('endTime', e.target.value)} />
                            </div>
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>Ημέρες (κενό = όλες)</label>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {DAYS.map((d) => (
                                    <button key={d.n} type="button" onClick={() => toggleDay(d.n)}
                                        style={{
                                            padding: '6px 10px', fontSize: 13, borderRadius: 6, cursor: 'pointer',
                                            border: '1px solid ' + (weekdays.includes(d.n) ? '#0D9A6A' : '#ccc'),
                                            background: weekdays.includes(d.n) ? '#E4F5EC' : '#fff',
                                        }}>
                                        {d.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Διάρκεια ραντεβού (λεπτά)</label>
                                <select style={fieldStyle} value={form.stepMinutes}
                                    onChange={(e) => setField('stepMinutes', Number(e.target.value))}>
                                    {[5, 10, 15, 20, 30, 60].map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <ActionBtn variant="default" onClick={loadPreview}
                                disabled={loading || !form.startDate || !form.endDate}>
                                {loading ? 'Υπολογισμός…' : 'Προεπισκόπηση'}
                            </ActionBtn>
                        </div>

                        {error && <p style={{ fontSize: 14, color: 'var(--crimson)' }}>{error}</p>}

                        {preview && !loading && (
                            <div style={boxStyle}>
                                <div style={rowStyle}><span>Ημέρες</span><strong>{preview.days}</strong></div>
                                <div style={rowStyle}><span>Ώρες ανά ημέρα</span><strong>{preview.windowsPerDay}</strong></div>
                                <div style={rowStyle}><span>Θα κλειστούν</span><strong>{preview.willBlock}</strong></div>
                                <div style={rowStyle}><span>Ήδη κλειστές</span><strong>{preview.alreadyBlocked}</strong></div>
                                <div style={rowStyle}>
                                    <span>Παραλείπονται (έχουν ραντεβού)</span>
                                    <strong>{preview.skippedForAppointment}</strong>
                                </div>
                                {preview.skippedForAppointment > 0 && (
                                    <p style={{ fontSize: 13, margin: '6px 0 0', color: 'var(--muted)' }}>
                                        Αυτές οι ώρες μένουν ανοιχτές. Για να ακυρωθούν τα ραντεβού, χρησιμοποιήστε
                                        τον καθαρισμό ημέρας, που κάνει και τις επιστροφές.
                                    </p>
                                )}
                            </div>
                        )}

                        {preview && !loading && (
                            <div style={{ marginBottom: 12 }}>
                                <label style={labelStyle}>Αιτία (υποχρεωτικό)</label>
                                <input type="text" style={fieldStyle} value={reason}
                                    placeholder="Π.χ. άδεια ιατρού"
                                    onChange={(e) => setReason(e.target.value)} />
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
                            <ActionBtn variant="default" onClick={handleUnblock}
                                disabled={running || !form.startDate || !form.endDate}>
                                Άρση φραγής
                            </ActionBtn>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <ActionBtn variant="default" onClick={handleClose}>Άκυρο</ActionBtn>
                                <ActionBtn variant="primary" onClick={handleRun}
                                    disabled={running || !preview || !reason.trim() || preview.willBlock === 0}>
                                    {running ? 'Εκτελείται…' : 'Κλείσιμο ' + (preview ? preview.willBlock : 0) + ' ωρών'}
                                </ActionBtn>
                            </div>
                        </div>

                        {!preview && touched && !loading && !error && (
                            <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 10 }}>
                                Κάντε προεπισκόπηση πριν το κλείσιμο.
                            </p>
                        )}
                    </>
                )}

                {result && (
                    <>
                        <div style={boxStyle}>
                            <div style={rowStyle}><span>Ημέρες</span><strong>{result.days}</strong></div>
                            <div style={rowStyle}><span>Κλείστηκαν</span><strong>{result.blocked}</strong></div>
                            <div style={rowStyle}><span>Ήταν ήδη κλειστές</span><strong>{result.alreadyBlocked}</strong></div>
                            <div style={rowStyle}>
                                <span>Παραλείφθηκαν (έχουν ραντεβού)</span>
                                <strong>{result.skippedForAppointment}</strong>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <ActionBtn variant="primary" onClick={handleClose}>Κλείσιμο</ActionBtn>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}
